'use es6';

import { BudgetTypes, MIN_DAILY_BUDGET_IN_USD, FileTypes, VideoErrors, Networks, DAYS_PER_WEEK, FBGeolocationTypes } from 'AdsUI/lib/Constants';
import { getDurationInDays } from 'AdsUI/lib/Utils';
import { toMoment } from 'UIComponents/core/SimpleDate';
import I18n from 'I18n';
import { ScheduleTypes } from 'AdsUI/records/ad-management/ScheduleRecord';
import AdWordsLanguages from 'AdsUI/datasets/adwords/languages';
export const isLeadAdBudgetTooLow = (network, budget, schedule, currencyCode) => {
  if (currencyCode !== 'USD') {
    return false;
  }

  const {
    type,
    value
  } = budget;

  switch (type) {
    case BudgetTypes.DAILY:
      {
        return value < MIN_DAILY_BUDGET_IN_USD[network];
      }

    case BudgetTypes.TOTAL:
      {
        if (network === Networks.LINKEDIN) {
          return value < MIN_DAILY_BUDGET_IN_USD[network];
        }

        const numberOfDays = getDurationInDays(schedule);
        return value / numberOfDays < MIN_DAILY_BUDGET_IN_USD[network];
      }

    default:
      {
        return false;
      }
  }
};
export const isScheduleTimeTooShort = ({
  startDate,
  endDate,
  startTime,
  endTime
}, {
  type: budgetType
}) => {
  // Daily budgets must be at least 24 hours
  if (!endDate || budgetType !== BudgetTypes.DAILY) {
    return false;
  }

  const startMoment = I18n.moment(Math.max(toMoment(startDate).add(startTime, 'minutes'), I18n.moment()));
  const endMoment = toMoment(endDate).add(endTime, 'minutes');
  const delta = endMoment.diff(startMoment, 'days', true);
  return delta < 1;
};
export const hasMediumThumbnail = file => file.get('type') === FileTypes.MOVIE && (!file.getIn(['meta', 'thumbs']) || // Newly uploaded will eventually have a medium thumbnail
!!file.getIn(['meta', 'thumbs', 'medium']));
export const isImageTooSmall = media => {
  return media.get('type') === FileTypes.IMG && (media.get('width') < 600 || media.get('height') < 600);
};
export const getLocationKeysByGeolocationType = (locations, type) => locations.filter(loc => loc.type === type).map(loc => loc.key).toArray();
export const getLocationCountryCodes = locations => locations.filter(loc => loc.type !== FBGeolocationTypes.COUNTRY_GROUP).map(loc => loc.type === FBGeolocationTypes.COUNTRY ? loc.key : loc.countryCode).toArray();
export const isValidWebsiteUrl = (url, network) => {
  if (url.indexOf('://') > -1) {
    // Prevent the user from putting their privacy policy on Gopher or FTP
    const protocol = url.split('://')[0].toLowerCase();

    if (protocol !== 'http' && protocol !== 'https') {
      return false;
    }
  } else if (network === Networks.LINKEDIN) {
    // LinkedIn requires a protocol
    return false;
  } // Ensure that the domain has a TLD and doesn't have .. or a space; this appears to be
  // all Facebook requires


  return url.indexOf('.') > -1 && url.indexOf('..') === -1 && url.indexOf(' ') === -1;
}; // https://www.facebook.com/business/ads-guide/video/facebook-feed/lead-generation

const MINIMUM_ASPECT_RATIO = {
  [Networks.FACEBOOK]: 1 / 4,
  [Networks.LINKEDIN]: 9 / 16
};
const MAXIMUM_ASPECT_RATIO = {
  [Networks.FACEBOOK]: 4 / 1,
  [Networks.LINKEDIN]: 16 / 9
};
const MINIMUM_VIDEO_SIDE = {
  [Networks.FACEBOOK]: 120,
  [Networks.LINKEDIN]: 360
};
export const getVideoErrors = (network, file) => {
  const width = file.get('width');
  const height = file.get('height');
  const aspectRatio = width / height;

  if (width < MINIMUM_VIDEO_SIDE[network] || height < MINIMUM_VIDEO_SIDE[network]) {
    return VideoErrors.DIMENSIONS;
  }

  if (aspectRatio < MINIMUM_ASPECT_RATIO[network] || aspectRatio > MAXIMUM_ASPECT_RATIO[network]) {
    return VideoErrors.ASPECT_RATIO;
  }

  return null;
};
export const getTotalCost = (budget, schedule) => {
  const {
    type,
    value
  } = budget;

  switch (type) {
    case BudgetTypes.DAILY:
      {
        const numberOfDays = !!schedule.endDate || schedule.endDateType === ScheduleTypes.DAY_MAX ? getDurationInDays(schedule) : DAYS_PER_WEEK;
        return value * numberOfDays;
      }

    case BudgetTypes.TOTAL:
      {
        return value;
      }

    default:
      {
        return null;
      }
  }
};
export const getMinimumBudget = (adAccount, budget, schedule) => {
  const {
    type
  } = budget;

  if (!adAccount) {
    return null;
  }

  if (adAccount.currencyCode !== 'USD') {
    return null;
  }

  if (type === BudgetTypes.DAILY || adAccount.network === Networks.LINKEDIN) {
    return MIN_DAILY_BUDGET_IN_USD[adAccount.network];
  } else {
    return MIN_DAILY_BUDGET_IN_USD[adAccount.network] * (schedule.startDateType === ScheduleTypes.FIXED_DATE ? getDurationInDays(schedule) : DAYS_PER_WEEK);
  }
};
export const getDefaultAdWordsLanguage = hsLocale => {
  const langCode = hsLocale.toLowerCase().substr(0, 2);
  const adWordsLanguage = AdWordsLanguages.values.find(lang => lang.code === langCode);

  if (adWordsLanguage) {
    return adWordsLanguage;
  } // All locales we support for date and number format but don't provide translations for,
  // we fall back to English. Therefore we should do that here as well.


  return AdWordsLanguages.values.find(lang => lang.code === 'en');
};